/**
 * Shared browse catalogue helpers — used by /browse and DrawerBrowsePanel.
 * MS619: sort, text search, occasion facets, and filter URL sync (Shopify/Amazon-style mini catalogue).
 */

import type { BrowseFilters, VendorCategory } from "@/components/browse/FilterRail";

export const BROWSE_ALLOWED_CATEGORIES: VendorCategory[] = [
  "cake",
  "hamper",
  "florist",
  "workshop",
  "spa",
  "experience",
  "meal",
  "coffee",
  "drinkware",
  "homewares",
  "plants",
  "books",
];

export type BrowseSort =
  | "featured"
  | "price_asc"
  | "price_desc"
  | "name_asc"
  | "newest";

export const BROWSE_SORT_OPTIONS: { value: BrowseSort; label: string }[] = [
  { value: "featured", label: "Featured" },
  { value: "price_asc", label: "Price: low to high" },
  { value: "price_desc", label: "Price: high to low" },
  { value: "name_asc", label: "Name: A–Z" },
  { value: "newest", label: "Newest" },
];

export type BrowseMomentTag = "new_home" | "housewarming" | "new_baby" | "sympathy";

export const BROWSE_MOMENT_OPTIONS: { value: BrowseMomentTag; label: string }[] = [
  { value: "new_home", label: "New home" },
  { value: "housewarming", label: "Housewarming" },
  { value: "new_baby", label: "New baby" },
  { value: "sympathy", label: "Sympathy" },
];

export type BrowsePageSize = 20 | 50 | 100;

export const BROWSE_PAGE_SIZE_OPTIONS: { value: BrowsePageSize; label: string }[] = [
  { value: 20, label: "20 per page" },
  { value: 50, label: "50 per page" },
  { value: 100, label: "100 per page" },
];

const ALLOWED_SORT: BrowseSort[] = ["featured", "price_asc", "price_desc", "name_asc", "newest"];
const ALLOWED_MOMENTS: BrowseMomentTag[] = ["new_home", "housewarming", "new_baby", "sympathy"];
const ALLOWED_PAGE_SIZES: BrowsePageSize[] = [20, 50, 100];

export function defaultBrowseFilters(): BrowseFilters {
  return {
    categories: [],
    priceMin: null,
    priceMax: null,
    dietary: [],
    maxLeadTimeDays: null,
    searchQuery: "",
    sort: "featured",
    momentTag: null,
    pageSize: 20,
    page: 1,
  };
}

export function filtersFromParams(params: URLSearchParams): BrowseFilters {
  const categories = (params.get("category") ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter((s): s is VendorCategory => BROWSE_ALLOWED_CATEGORIES.includes(s as VendorCategory));

  const priceMin = params.get("priceMin");
  const priceMax = params.get("priceMax");
  const budget = params.get("budget");
  const min = priceMin !== null ? Number(priceMin) : null;
  const max = priceMax !== null ? Number(priceMax) : budget !== null ? Number(budget) : null;

  const dietary = (params.get("dietary") ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

  const leadTimeRaw = params.get("leadTimeMax");
  const leadTimeNum = leadTimeRaw !== null ? Number(leadTimeRaw) : null;
  const maxLeadTimeDays =
    leadTimeNum !== null && [1, 3, 7].includes(leadTimeNum) ? leadTimeNum : null;

  const sortRaw = params.get("sort") as BrowseSort | null;
  const sort = sortRaw && ALLOWED_SORT.includes(sortRaw) ? sortRaw : "featured";

  const momentRaw = params.get("moment") as BrowseMomentTag | null;
  const momentTag = momentRaw && ALLOWED_MOMENTS.includes(momentRaw) ? momentRaw : null;

  const searchQuery = (params.get("q") ?? "").trim().slice(0, 120);

  const pageSizeRaw = Number(params.get("pageSize"));
  const pageSize = ALLOWED_PAGE_SIZES.includes(pageSizeRaw as BrowsePageSize)
    ? (pageSizeRaw as BrowsePageSize)
    : 20;

  const pageRaw = Number(params.get("page"));
  const page = Number.isFinite(pageRaw) && pageRaw >= 1 ? Math.floor(pageRaw) : 1;

  return {
    categories,
    priceMin: Number.isFinite(min as number) ? min : null,
    priceMax: Number.isFinite(max as number) ? max : null,
    dietary,
    maxLeadTimeDays,
    searchQuery,
    sort,
    momentTag,
    pageSize,
    page,
  };
}

export function filtersToParams(f: BrowseFilters, existing: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(existing);
  next.delete("budget");
  if (f.categories.length > 0) next.set("category", f.categories.join(","));
  else next.delete("category");
  if (f.priceMin !== null) next.set("priceMin", String(f.priceMin));
  else next.delete("priceMin");
  if (f.priceMax !== null) next.set("priceMax", String(f.priceMax));
  else next.delete("priceMax");
  if (f.dietary.length > 0) next.set("dietary", f.dietary.join(","));
  else next.delete("dietary");
  if (f.maxLeadTimeDays !== null) next.set("leadTimeMax", String(f.maxLeadTimeDays));
  else next.delete("leadTimeMax");
  if (f.searchQuery.trim()) next.set("q", f.searchQuery.trim());
  else next.delete("q");
  if (f.sort !== "featured") next.set("sort", f.sort);
  else next.delete("sort");
  if (f.momentTag) next.set("moment", f.momentTag);
  else next.delete("moment");
  if (f.pageSize !== 20) next.set("pageSize", String(f.pageSize));
  else next.delete("pageSize");
  if (f.page > 1) next.set("page", String(f.page));
  else next.delete("page");
  return next;
}

/** Reset to page 1 when a facet changes (not sort/pageSize). */
export function withBrowseFacetChange(
  next: BrowseFilters,
  prev: BrowseFilters,
): BrowseFilters {
  const facetChanged =
    next.categories.join(",") !== prev.categories.join(",") ||
    next.priceMin !== prev.priceMin ||
    next.priceMax !== prev.priceMax ||
    next.dietary.join(",") !== prev.dietary.join(",") ||
    next.maxLeadTimeDays !== prev.maxLeadTimeDays ||
    next.momentTag !== prev.momentTag ||
    next.searchQuery.trim() !== prev.searchQuery.trim();

  if (facetChanged) return { ...next, page: 1 };
  if (next.pageSize !== prev.pageSize) return { ...next, page: 1 };
  return next;
}

export function describeFilters(f: BrowseFilters): string {
  const parts: string[] = [];
  if (f.categories.length === 1) parts.push(f.categories[0] + "s");
  else if (f.categories.length > 1) parts.push(`${f.categories.length} categories`);

  if (f.priceMin !== null && f.priceMax !== null) parts.push(`$${f.priceMin}–$${f.priceMax}`);
  else if (f.priceMax !== null) parts.push(`under $${f.priceMax}`);
  else if (f.priceMin !== null) parts.push(`from $${f.priceMin}`);

  if (f.dietary.length > 0) parts.push(`${f.dietary.length} dietary`);
  if (f.maxLeadTimeDays === 1) parts.push("by tomorrow");
  else if (f.maxLeadTimeDays !== null) parts.push(`within ${f.maxLeadTimeDays} days`);
  if (f.momentTag) {
    const label = BROWSE_MOMENT_OPTIONS.find((m) => m.value === f.momentTag)?.label;
    if (label) parts.push(label.toLowerCase());
  }
  if (f.searchQuery.trim()) parts.push(`matching “${f.searchQuery.trim()}”`);
  return parts.join(" · ");
}

/** Escape `%` and `_` for PostgREST ilike patterns. */
export function escapeIlikePattern(raw: string): string {
  return raw.replace(/\\/g, "\\\\").replace(/%/g, "\\%").replace(/_/g, "\\_");
}

export interface BrowseFilterableCard {
  vendor_category?: string | null;
  price_aud: number;
  dietary_tags?: string[] | null;
  lead_time_days?: number | null;
  is_voucher?: boolean | null;
  name?: string;
  description?: string | null;
  moment_tags?: string[] | null;
}

/** Client-side filter walk (popular picks row). Mirrors fetchPage semantics. */
export function cardMatchesBrowseFilters(
  card: BrowseFilterableCard,
  filters: BrowseFilters,
): boolean {
  if (
    filters.categories.length > 0 &&
    !filters.categories.includes(card.vendor_category as VendorCategory)
  ) {
    return false;
  }
  if (filters.priceMin !== null && card.price_aud < filters.priceMin) return false;
  if (filters.priceMax !== null && card.price_aud > filters.priceMax) return false;
  if (filters.dietary.length > 0) {
    const tags = card.dietary_tags ?? [];
    if (!filters.dietary.every((d) => tags.includes(d))) return false;
  }
  if (filters.maxLeadTimeDays !== null) {
    const lead = card.lead_time_days ?? null;
    const isVoucher = card.is_voucher === true;
    if (!isVoucher && lead !== null && lead > filters.maxLeadTimeDays) return false;
  }
  if (filters.momentTag) {
    const tags = card.moment_tags ?? [];
    if (tags.length > 0 && !tags.includes(filters.momentTag)) return false;
  }
  const q = filters.searchQuery.trim().toLowerCase();
  if (q) {
    const hay = `${card.name ?? ""} ${card.description ?? ""}`.toLowerCase();
    if (!hay.includes(q)) return false;
  }
  return true;
}

export function countActiveBrowseFilters(f: BrowseFilters): number {
  return (
    f.categories.length +
    (f.priceMin !== null || f.priceMax !== null ? 1 : 0) +
    f.dietary.length +
    (f.maxLeadTimeDays !== null ? 1 : 0) +
    (f.momentTag ? 1 : 0) +
    (f.searchQuery.trim() ? 1 : 0)
  );
}
